import React, { createContext, useContext, useMemo, ReactNode } from 'react';

import { ProductCardTextAlignmentOption } from 'src/gql/graphql';
import {
  ProductCardProduct,
  ProductCardImage,
  ProductCardBadges,
  ProductCardPricing,
  ProductCardOptions,
  ProductCardLayout,
  ListPriceConfig,
  CollectionBadgeViewData,
} from './types';

export type CardContextValue = {
  productName: string;
  brandName: string;
  href: string;
  strainType: string | null;
  potency: string[];
  avgRating: number | null;
  reviewCount: number | null;
  image: ProductCardImage;
  isStaffPick: boolean;
  isSponsored: boolean;
  isSpecialOffer: boolean;
  isComingSoon: boolean;
  specialNames: string[];
  hideSalesLanguage: boolean;
  collectionBadge: CollectionBadgeViewData | null;
  displayPrice: string;
  standardPrice: string | null;
  discount: string | null;
  showWeight: boolean;
  weight: string;
  buttonCopy: string;
  listPriceEnabled: boolean;
  hideStrikethrough: boolean;
  formatStandardPrice: (price: string) => string;
  isEmbeddedCarousel: boolean;
  fullWidthImages: boolean;
  textAlignment: ProductCardTextAlignmentOption;
  hideSponsoredTag: boolean;
  options?: ProductCardOptions;
  listPrice?: ListPriceConfig;
  onLinkClick: () => void;
  onAddToCart?: () => void;
  onOptionClick?: (value: string) => void;
};

const CardContext = createContext<CardContextValue | null>(null);

export function useCardContext(): CardContextValue {
  const ctx = useContext(CardContext);
  if (!ctx) {
    throw new Error('useCardContext must be used within a CardProvider');
  }
  return ctx;
}

type CardProviderProps = {
  children: ReactNode;
  product: ProductCardProduct;
  image: ProductCardImage;
  badges: ProductCardBadges;
  pricing: ProductCardPricing;
  layout: ProductCardLayout;
  hideSponsoredTag: boolean;
  options?: ProductCardOptions;
  listPrice?: ListPriceConfig;
  onLinkClick: () => void;
  onAddToCart?: () => void;
  onOptionClick?: (value: string) => void;
};

/** Distributes pre-computed product card data to sub-components via context. */
export function CardProvider({
  children,
  product,
  image,
  badges,
  pricing,
  layout,
  hideSponsoredTag,
  options,
  listPrice,
  onLinkClick,
  onAddToCart,
  onOptionClick,
}: CardProviderProps): JSX.Element {
  const value = useMemo<CardContextValue>(
    () => ({
      productName: product.name,
      brandName: product.brandName,
      href: product.href,
      strainType: product.strainType,
      potency: product.potency,
      avgRating: product.avgRating,
      reviewCount: product.reviewCount,
      image,
      ...badges,
      ...pricing,
      ...layout,
      hideSponsoredTag,
      options,
      listPrice,
      onLinkClick,
      onAddToCart,
      onOptionClick,
    }),
    [
      product,
      image,
      badges,
      pricing,
      layout,
      hideSponsoredTag,
      options,
      listPrice,
      onLinkClick,
      onAddToCart,
      onOptionClick,
    ]
  );

  return <CardContext.Provider value={value}>{children}</CardContext.Provider>;
}
